import React, { useState, useEffect, useMemo } from "react";
import { Form, Input, Select, Button, Space, Row, Col, DatePicker, message } from "antd";
import { SearchOutlined, ReloadOutlined, SyncOutlined, SettingOutlined } from "@ant-design/icons";
import apiClient from "../../../../api/axios";
import AircraftSelector from "../../../../components/common/AircraftSelector";
import { masterDataService } from "../../../../api/enhancedApi";
import { STANDARD_COST_TYPE_LABELS } from "../../constants";
import AircraftTrackingModal from "./AircraftTrackingModal";

const { RangePicker } = DatePicker;

const LegCostFilter = ({ onSearch, onReset, onRefresh, defaultDateRange }) => {
    const [form] = Form.useForm();
    const [costTypes, setCostTypes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [airportOptions, setAirportOptions] = useState([]);
    const [loadingAirports, setLoadingAirports] = useState(false);
    const [aircraftTrackingOpen, setAircraftTrackingOpen] = useState(false);

    // 获取费用类型列表
    const fetchCostTypes = async () => {
        try {
            setLoading(true);
            const response = await apiClient.get("/v1/settlement/cost-types-used");
            setCostTypes(response.data || []);
        } catch (error) {
            console.error("获取费用类型失败:", error);
            message.error("获取费用类型失败: " + (error.message || "未知错误"));
        } finally {
            setLoading(false);
        }
    };

    // 获取机场列表
    const fetchAirports = async () => {
        try {
            setLoadingAirports(true);
            
            // 先检查全局缓存中是否已有机场数据
            if (window.cachedAirports && Array.isArray(window.cachedAirports) && window.cachedAirports.length > 0) {
                const options = window.cachedAirports.map((airport) => ({
                    value: airport.iata_code,
                    label: `${airport.iata_code}${airport.icao_code ? ` - ${airport.icao_code}` : ""}${airport.airport_name ? ` (${airport.airport_name})` : ""}`,
                }));
                setAirportOptions(options);
                setLoadingAirports(false);
                return;
            }

            // 如果缓存中没有，从API获取
            const response = await masterDataService.airport.getAirportList({ page: 1, page_size: 500 });
            const airportList = response?.data?.items || [];
            
            if (airportList.length > 0) {
                // 缓存到全局
                window.cachedAirports = airportList;
                
                const options = airportList.map((airport) => ({
                    value: airport.iata_code,
                    label: `${airport.iata_code}${airport.icao_code ? ` - ${airport.icao_code}` : ""}${airport.airport_name ? ` (${airport.airport_name})` : ""}`,
                }));
                setAirportOptions(options);
            }
        } catch (error) {
            console.error("获取机场列表失败:", error);
            message.error("获取机场列表失败: " + (error.message || "未知错误"));
        } finally {
            setLoadingAirports(false);
        }
    };

    // 🚨 修复：分离费用类型加载和日期范围设置，避免重复调用
    
    // 组件初始化时获取费用类型列表和机场列表（只执行一次）
    useEffect(() => {
        fetchCostTypes();
        fetchAirports();
    }, []); // 空依赖数组，只在组件挂载时执行一次
    
    // 设置默认日期范围（当defaultDateRange变化时执行）
    useEffect(() => {
        if (defaultDateRange && defaultDateRange.length === 2) {
            form.setFieldsValue({
                date_range: defaultDateRange
            });
        }
    }, [defaultDateRange, form]); // 依赖defaultDateRange和form

    const costTypeOptions = useMemo(() => {
        const seen = new Set();
        const options = [];
        for (const type of costTypes) {
            const rawValue = type?.value == null ? "" : String(type.value).trim();
            if (!rawValue) continue;
            const key = rawValue.toUpperCase();
            if (seen.has(key)) continue;
            seen.add(key);
            if (key === "ALL") {
                options.push({ value: "all", label: "全部" });
                continue;
            }
            options.push({
                value: key,
                label: STANDARD_COST_TYPE_LABELS[key] || type.label || key,
            });
        }
        return options;
    }, [costTypes]);

    // 处理搜索
    const handleSearch = () => {
        const values = form.getFieldsValue();

        // 处理多选飞机号：将数组转换为逗号分隔的字符串
        if (values.aircraft_registration && Array.isArray(values.aircraft_registration)) {
            values.aircraft_registration = values.aircraft_registration.join(',');
        }

        onSearch(values);
    };

    // 处理重置 - 重置到默认日期范围
    const handleReset = () => {
        form.resetFields();
        
        // 🚀 重置后恢复默认日期范围
        if (defaultDateRange && defaultDateRange.length === 2) {
            form.setFieldsValue({
                date_range: defaultDateRange
            });
        }
        
        onReset();
    };

    // 处理回车搜索
    const handleKeyPress = (e) => {
        if (e.key === "Enter") {
            handleSearch();
        }
    };

    return (
        <Form form={form} layout="inline" className="filter-form">
            <Row gutter={[16, 16]} style={{ width: "100%" }}>
                {/* 第一行：筛选条件 */}
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item name="aircraft_registration" label="飞机号">
                        <AircraftSelector
                            mode="multiple"
                            placeholder="请选择或输入飞机号(支持多选)"
                            allowClear
                            showSearch
                            displayMode="simple"
                            maxTagCount="responsive"
                            style={{ width: '100%' }}
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item name="flight_number" label="航班号">
                        <Input
                            placeholder="请输入航班号"
                            allowClear
                            onKeyPress={handleKeyPress}
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item name="cost_type" label="费用类型">
                        <Select
                            placeholder="请选择费用类型"
                            allowClear
                            showSearch
                            loading={loading}
                            optionFilterProp="label"
                            options={costTypeOptions}
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item name="operation_date" label="执行日期">
                        <DatePicker
                            style={{ width: "100%" }}
                            placeholder="选择日期"
                            format="YYYY-MM-DD"
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item name="date_range" label="日期范围">
                        <RangePicker
                            style={{ width: "100%" }}
                            placeholder={["开始日期", "结束日期"]}
                            format="YYYY-MM-DD"
                        />
                    </Form.Item>
                </Col>

                {/* 第二行：机场筛选 */}
                <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item name="departure_airport" label="出发机场">
                        <Select
                            showSearch
                            placeholder="选择出发机场"
                            allowClear
                            optionFilterProp="label"
                            optionLabelProp="value"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                            options={airportOptions}
                            loading={loadingAirports}
                        />
                    </Form.Item>
                </Col>

                <Col xs={24} sm={12} md={8} lg={6}>
                    <Form.Item name="arrival_airport" label="到达机场">
                        <Select
                            showSearch
                            placeholder="选择到达机场"
                            allowClear
                            optionFilterProp="label"
                            optionLabelProp="value"
                            filterOption={(input, option) =>
                                (option?.label || "").toLowerCase().includes(input.toLowerCase())
                            }
                            options={airportOptions}
                            loading={loadingAirports}
                        />
                    </Form.Item>
                </Col>

                {/* 配置飞机按钮:打开本组件内部弹窗,不污染父级页面 */}
                <Col xs={24} sm={12} md={8} lg={6} style={{ display: "flex", alignItems: "center" }}>
                    <Button
                        icon={<SettingOutlined />}
                        onClick={() => setAircraftTrackingOpen(true)}
                        title="勾选哪些飞机要进结算(不勾的航班/账单全部隐藏)"
                    >
                        配置结算飞机
                    </Button>
                </Col>

                {/* 第三行：操作按钮 */}
                <Col xs={24}>
                    <Form.Item>
                        <Space>
                            <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                                搜索
                            </Button>
                            <Button icon={<ReloadOutlined />} onClick={handleReset}>
                                重置
                            </Button>
                            <Button icon={<SyncOutlined />} onClick={onRefresh}>
                                刷新
                            </Button>
                        </Space>
                    </Form.Item>
                </Col>
            </Row>

            <AircraftTrackingModal
                open={aircraftTrackingOpen}
                onClose={() => setAircraftTrackingOpen(false)}
                onSaved={() => {
                    // 配置变更后自动刷新主列表(让屏蔽生效)
                    if (typeof onRefresh === "function") {
                        onRefresh();
                    }
                }}
            />
        </Form>
    );
};

export default LegCostFilter;

